import { useCallback, useEffect, useId, useRef, useState } from "react";
import { useAudioPlayback } from "../contexts/AudioPlaybackContext";
import { formatDurationSec } from "../utils/dateUtils";
import "./AudioPlayer.css";

interface AudioPlayerProps {
	blob: Blob;
	mimeType: string;
	onDelete?: () => void;
	compact?: boolean;
	compactLabel?: string;
}

export function AudioPlayer({
	blob,
	mimeType,
	onDelete,
	compact = false,
	compactLabel,
}: AudioPlayerProps) {
	const playerId = useId();
	const audioRef = useRef<HTMLAudioElement | null>(null);
	const [isPlaying, setIsPlaying] = useState(false);
	const [currentTime, setCurrentTime] = useState(0);
	const [duration, setDuration] = useState(0);
	const [error, setError] = useState<string | null>(null);
	const [audioUrl, setAudioUrl] = useState<string | null>(null);

	const { registerAudio, unregisterAudio, startPlayback } = useAudioPlayback();

	// Register this audio element with the playback coordinator
	useEffect(() => {
		registerAudio(playerId, audioRef);
		return () => unregisterAudio(playerId);
	}, [playerId, registerAudio, unregisterAudio]);

	// Create object URL for the blob
	useEffect(() => {
		const url = URL.createObjectURL(blob);
		setAudioUrl(url);

		return () => {
			URL.revokeObjectURL(url);
		};
	}, [blob]);

	// Set up audio element event handlers
	// Re-run when audioUrl changes because audio element is conditionally rendered
	useEffect(() => {
		const audio = audioRef.current;
		if (!audio) return;

		const handleTimeUpdate = () => {
			setCurrentTime(audio.currentTime);
		};

		const handleDurationChange = () => {
			if (Number.isFinite(audio.duration)) {
				setDuration(audio.duration);
			}
		};

		const handleEnded = () => {
			setIsPlaying(false);
			setCurrentTime(0);
		};

		const handleError = () => {
			setError("Failed to load audio");
			setIsPlaying(false);
		};

		// Sync isPlaying state when audio is paused externally (by another player)
		const handlePause = () => {
			setIsPlaying(false);
		};

		const handlePlay = () => {
			setIsPlaying(true);
		};

		audio.addEventListener("timeupdate", handleTimeUpdate);
		audio.addEventListener("durationchange", handleDurationChange);
		audio.addEventListener("loadedmetadata", handleDurationChange);
		audio.addEventListener("ended", handleEnded);
		audio.addEventListener("error", handleError);
		audio.addEventListener("pause", handlePause);
		audio.addEventListener("play", handlePlay);

		return () => {
			audio.removeEventListener("timeupdate", handleTimeUpdate);
			audio.removeEventListener("durationchange", handleDurationChange);
			audio.removeEventListener("loadedmetadata", handleDurationChange);
			audio.removeEventListener("ended", handleEnded);
			audio.removeEventListener("error", handleError);
			audio.removeEventListener("pause", handlePause);
			audio.removeEventListener("play", handlePlay);
		};
	}, [audioUrl]);

	const handlePlayPause = useCallback(() => {
		const audio = audioRef.current;
		if (!audio) return;

		if (isPlaying) {
			audio.pause();
		} else {
			// Tell the coordinator to pause all other audio before we start
			startPlayback(playerId);
			audio.play().catch(() => {
				setError("Failed to play audio");
			});
		}
	}, [isPlaying, playerId, startPlayback]);

	const handleSeek = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const audio = audioRef.current;
			if (!audio) return;

			const newTime = (Number.parseFloat(e.target.value) / 100) * duration;
			audio.currentTime = newTime;
			setCurrentTime(newTime);
		},
		[duration],
	);

	const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

	if (error) {
		return (
			<div className="audio-player audio-player-error">
				<span className="audio-player-error-text">{error}</span>
			</div>
		);
	}

	// Compact mode: just play button + label
	if (compact) {
		return (
			<div className="audio-player audio-player-compact">
				{audioUrl && (
					<audio ref={audioRef} src={audioUrl} preload="metadata">
						<track kind="captions" />
					</audio>
				)}
				<button
					type="button"
					className="audio-player-play"
					onClick={handlePlayPause}
					aria-label={isPlaying ? "Pause" : "Play"}
				>
					{isPlaying ? "\u275A\u275A" : "\u25B6"}
				</button>
				<span className="audio-player-compact-label">
					{compactLabel || formatDurationSec(duration)}
				</span>
				{onDelete && (
					<button
						type="button"
						className="audio-player-delete-compact"
						onClick={onDelete}
						aria-label="Delete recording"
					>
						{"\u2715"}
					</button>
				)}
			</div>
		);
	}

	return (
		<div className="audio-player">
			{audioUrl && (
				<audio ref={audioRef} src={audioUrl} preload="metadata">
					<track kind="captions" />
				</audio>
			)}

			<button
				type="button"
				className="audio-player-play"
				onClick={handlePlayPause}
				aria-label={isPlaying ? "Pause" : "Play"}
			>
				{isPlaying ? "\u275A\u275A" : "\u25B6"}
			</button>

			<div className="audio-player-progress-container">
				<input
					type="range"
					className="audio-player-progress"
					min="0"
					max="100"
					value={progress}
					onChange={handleSeek}
					aria-label="Seek"
				/>
			</div>

			<span className="audio-player-time">
				{formatDurationSec(currentTime)} / {formatDurationSec(duration)}
			</span>

			{onDelete && (
				<button
					type="button"
					className="audio-player-delete"
					onClick={onDelete}
					aria-label="Delete recording"
				>
					{"\u2715"}
				</button>
			)}
		</div>
	);
}
